/**
 * Aufrufe der Stammdatenverwaltung.
 *
 * Die acht Stammdatenarten werden ueber eine gemeinsame Beschreibung
 * angesprochen. Das spiegelt den Server: Dort ist es eine Verwaltung mit acht
 * Auspraegungen, nicht acht Verwaltungen.
 */

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';

export type Queue = components['schemas']['QueueRead'];
export type TicketState = components['schemas']['StateRead'];
export type TicketType = components['schemas']['TypeRead'];
export type TicketPriority = components['schemas']['PriorityRead'];
export type TicketImpact = components['schemas']['ImpactRead'];
export type TicketUrgency = components['schemas']['UrgencyRead'];
export type Tag = components['schemas']['TagRead'];
export type ActivityType = components['schemas']['ActivityTypeRead'];
export type CategoryTree = components['schemas']['CategoryTree'];
export type PriorityMatrix = components['schemas']['MatrixRead'];
export type MatrixCell = components['schemas']['MatrixCell'];

/** Ein Stammdatensatz, soweit die gemeinsame Verwaltung ihn kennt. */
export interface MasterDataEntry {
  id: string;
  name: string;
  is_default?: boolean;
  is_active?: boolean;
  sort_order?: number;
  [key: string]: unknown;
}

/**
 * Die Pfade der acht Arten.
 *
 * Als Konstante und nicht aus dem Namen abgeleitet: Der Pfad ist Teil der
 * Schnittstelle, und ein aus dem Namen geratener Pfad braeche stillschweigend,
 * sobald jemand eine Art umbenennt.
 */
export const MASTER_DATA_PATHS = {
  queue: '/queues',
  state: '/ticket-states',
  type: '/ticket-types',
  priority: '/ticket-priorities',
  impact: '/ticket-impacts',
  urgency: '/ticket-urgencies',
  tag: '/tags',
  activity_type: '/activity-types',
} as const;

export type MasterDataKind = keyof typeof MASTER_DATA_PATHS;

export function listMasterData(kind: MasterDataKind): Promise<MasterDataEntry[]> {
  return request<MasterDataEntry[]>(MASTER_DATA_PATHS[kind]);
}

export function createMasterData(
  kind: MasterDataKind,
  payload: Record<string, unknown>,
): Promise<MasterDataEntry> {
  return request<MasterDataEntry>(MASTER_DATA_PATHS[kind], { method: 'POST', body: payload });
}

export function updateMasterData(
  kind: MasterDataKind,
  id: string,
  payload: Record<string, unknown>,
): Promise<MasterDataEntry> {
  return request<MasterDataEntry>(`${MASTER_DATA_PATHS[kind]}/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteMasterData(kind: MasterDataKind, id: string): Promise<undefined> {
  return request<undefined>(`${MASTER_DATA_PATHS[kind]}/${id}`, { method: 'DELETE' });
}

// --- Kategorien -------------------------------------------------------------

export function listCategories(): Promise<CategoryTree[]> {
  return request<CategoryTree[]>('/ticket-categories');
}

export function createCategory(payload: {
  name: string;
  parent_id?: string | null;
}): Promise<CategoryTree> {
  return request<CategoryTree>('/ticket-categories', { method: 'POST', body: payload });
}

export function updateCategory(
  id: string,
  payload: Record<string, unknown>,
): Promise<CategoryTree> {
  return request<CategoryTree>(`/ticket-categories/${id}`, { method: 'PATCH', body: payload });
}

export function deleteCategory(id: string): Promise<undefined> {
  return request<undefined>(`/ticket-categories/${id}`, { method: 'DELETE' });
}

// --- Prioritaetsmatrix ------------------------------------------------------

export function getPriorityMatrix(): Promise<PriorityMatrix> {
  return request<PriorityMatrix>('/priority-matrix');
}

/**
 * Setzt die Matrix vollstaendig.
 *
 * Ersetzend und nicht zellenweise -- eine Luecke waere ein Ticket, dessen
 * Prioritaet sich nicht ermitteln laesst. Die Oberflaeche schickt deshalb
 * immer das ganze Raster.
 */
export function replacePriorityMatrix(cells: MatrixCell[]): Promise<PriorityMatrix> {
  return request<PriorityMatrix>('/priority-matrix/actions/replace', {
    method: 'POST',
    body: { cells },
  });
}
